import { ArrowLeft, Footprints, Info } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Progress } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/progress";
import { Badge } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const weeklyData = [
  { day: "Mon", minutes: 45 },
  { day: "Tue", minutes: 20 },
  { day: "Wed", minutes: 60 },
  { day: "Thu", minutes: 15 },
  { day: "Fri", minutes: 10 },
  { day: "Sat", minutes: 30 },
  { day: "Sun", minutes: 10 },
];

const ActivityRecovery = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness2/inputs")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            Activity Recovery
          </h1>
        </div>

        {/* Current Value */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-muted">
              <Footprints className="h-7 w-7 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <p className="mb-1 text-sm text-muted-foreground">
                Today's Recovery Activity
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-black text-foreground">Low</span>
              </div>
              <p className="text-xs text-muted-foreground">
                ~10 min active recovery
              </p>
            </div>
            <Badge className="rounded-full border-0 bg-muted text-xs text-muted-foreground">
              Neutral
            </Badge>
          </div>
        </Card>

        {/* Buffer Strength */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Stress Buffer Strength
          </h3>
          <Progress value={35} className="mb-2 h-3" />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>Minimal</span>
            <span>Moderate</span>
            <span>Strong</span>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Light movement can modestly buffer physiological load.
          </p>
        </Card>

        {/* Weekly Trend */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Weekly Active Recovery (min)
          </h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="hsl(var(--border))"
                />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                  formatter={(value: number) => [`${value} min`, "Recovery"]}
                />
                <Bar
                  dataKey="minutes"
                  fill="hsl(var(--chart-2))"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* What This Means */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Info className="text-info h-5 w-5" />
            <h3 className="font-semibold text-foreground">What This Means</h3>
          </div>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">Active recovery</strong>{" "}
              refers to low-intensity movement like walking, stretching, or yoga
              that helps your body process stress hormones and restore balance.
            </p>
            <p>
              <strong className="text-foreground">
                With low activity today
              </strong>
              , your body has limited buffering against physiological stress.
              Even 20-30 minutes of gentle movement can meaningfully reduce your
              stress index.
            </p>
            <p>
              <strong className="text-foreground">
                The effect is modest but real:
              </strong>{" "}
              active recovery improves circulation, promotes parasympathetic
              activation, and can lower cortisol levels within hours.
            </p>
          </div>
        </Card>

        {/* Tips */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-foreground">
            Quick Recovery Activities
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-chart-2">•</span>
              <span>Take a 20-minute walk after lunch</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-2">•</span>
              <span>Do 10 minutes of gentle stretching</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-2">•</span>
              <span>Try a short yoga flow (15 min)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-2">•</span>
              <span>Use a foam roller for 5-10 minutes</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default ActivityRecovery;
